import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { formAtom } from "@frontend/lib/form";
import { text } from "@frontend/lib/form/validators";
import { ApiError } from "@frontend/lib/http";
import { loadReferences } from "@frontend/lib/references";
import { msg } from "@lingui/core/macro";

import { projectSchema } from "../schemas/project";
import type { PlannedExpenseValues, ProjectValues } from "../schemas/project";
import { projectApi } from "../services";
import type {
  ClientScope,
  PickerOption,
  Project,
  ProjectOptions,
  ProjectType,
} from "../types";
import { toProjectValues } from "../utils/payload";

export type ProjectEditorState = {
  projectId: number | null;
  type: ProjectType;
  project: Project | null;
  options: ProjectOptions;
  clientScope: ClientScope;
  loading: boolean;
  scopeLoading: boolean;
  saving: boolean;
  conflict: boolean;
  error: string | null;
};

const emptyOptions: ProjectOptions = {
  statuses: [],
  serviceGroups: [],
  affiliates: [],
  taskTypes: [],
  unitTypes: [],
  accountantTypes: [],
  clientContactTypes: [],
  sites: [],
  expenseRecurrences: [],
  expenseTypes: [],
  paymentTypes: [],
  paymentPeriods: [],
};

export const emptyProject = (type: ProjectType): ProjectValues => ({
  type,
  lockVersion: null,
  name: null,
  statusId: null,
  serviceGroupId: null,
  clientId: null,
  locationId: null,
  deliveryLocationId: null,
  billingAddressId: null,
  billingContactId: null,
  billingContactInBill: false,
  billingAttorneyId: null,
  billingClientReference: null,
  billingClientReferenceInBill: false,
  billingWording: null,
  billingComments: null,
  comments: null,
  quotationNumber: null,
  billingTypeJustification: null,
  clientContacts: [],
  accountants: [],
  commercialCondition: {
    paymentTypeId: null,
    interventionPaymentPeriodId: null,
    paymentEmail: null,
    paymentEmailCopyTo: null,
  },
  subscription: {
    initialAffiliateId: null,
    billingProjectId: null,
  },
  intervention: {
    taskTypeId: null,
    subTaskTypeId: null,
    dateBegin: null,
    dateEnd: null,
    interventionDays: null,
    refundLunch: false,
    refundRide: false,
    rideDuration: null,
    units: null,
    unitTypeId: null,
    voucherNumber: null,
    recurrence: null,
    exploitationComments: null,
    estimatedHours: null,
    estimatedDays: null,
    estimatedWorkers: null,
    estimatedValuedHours: null,
    startingSiteId: null,
    subscriptionId: null,
    plannedExpenses: [],
    dailySchedule: { arrival: null, departure: null, hours: [] },
  },
});

const emptyClientScope: ClientScope = {
  contactId: null,
  locations: [],
  billingAddresses: [],
  contacts: [],
};

export const initialProjectEditorState: ProjectEditorState = {
  projectId: null,
  type: "intervention",
  project: null,
  options: emptyOptions,
  clientScope: emptyClientScope,
  loading: true,
  scopeLoading: false,
  saving: false,
  conflict: false,
  error: null,
};

export const projectForm = formAtom(
  projectSchema,
  emptyProject("intervention"),
  {
    normalizeNull: false,
  },
);

export const projectEditorAtom = atomWithImmer<ProjectEditorState>(
  initialProjectEditorState,
);

export const editorTabAtom = atom("main");

export const optionsAtom = atom((get) => get(projectEditorAtom).options);

const plannedExpenses = projectForm.fields.intervention.fields.plannedExpenses;

export type EditedExpense = {
  index: number;
  created: boolean;
};

export const editedExpenseAtom = atom<EditedExpense | null>(null);

const emptyExpense = (): PlannedExpenseValues => ({
  id: null,
  typeId: null,
  quantity: null,
  unitPrice: null,
  recurrenceId: null,
});

export const addPlannedExpenseAtom = atom(null, (get, set) => {
  const index = get(plannedExpenses.valueAtom).length;

  set(plannedExpenses.insert, { value: emptyExpense() });

  set(editedExpenseAtom, { index, created: true });
});

export const cancelExpenseEditAtom = atom(
  null,
  (get, set, snapshot: PlannedExpenseValues | null) => {
    const edited = get(editedExpenseAtom);

    if (null === edited) {
      return;
    }

    const itemAtom = get(plannedExpenses.itemAtomsAtom)[edited.index];

    if (itemAtom) {
      if (edited.created) {
        set(plannedExpenses.remove, itemAtom);
      } else if (null !== snapshot) {
        set(itemAtom, snapshot);
      }
    }

    set(editedExpenseAtom, null);
  },
);

export const clientScopeAtom = atom(
  (get) => get(projectEditorAtom).clientScope,
);

export const scopeLoadingAtom = atom(
  (get) => get(projectEditorAtom).scopeLoading,
);

export const loadClientScopeAtom = atom(
  null,
  async (
    get,
    set,
    clientId: number | null,
    contactId: number | null,
  ): Promise<void> => {
    if (null === clientId || null === contactId) {
      set(projectEditorAtom, (state) => {
        state.clientScope = emptyClientScope;
        state.scopeLoading = false;
      });

      return;
    }

    if (get(projectEditorAtom).clientScope.contactId === contactId) {
      return;
    }

    set(projectEditorAtom, (state) => {
      state.scopeLoading = true;
    });

    try {
      const [locations, billingAddresses, contacts] = await Promise.all([
        projectApi.fetchLocations(contactId),
        projectApi.fetchBillingAddresses(contactId),
        projectApi.fetchClientContacts(clientId),
      ]);

      set(projectEditorAtom, (state) => {
        state.clientScope = {
          contactId,
          locations,
          billingAddresses,
          contacts,
        };
      });
    } finally {
      set(projectEditorAtom, (state) => {
        state.scopeLoading = false;
      });
    }
  },
);

export const billingProjectsAtom = atom<PickerOption[]>([]);

export const loadBillingProjectsAtom = atom(
  null,
  async (_get, set, clientId: number | null, projectId: number | null) => {
    if (null === clientId) {
      set(billingProjectsAtom, []);

      return;
    }

    try {
      const projects = await projectApi.fetchClientSubscriptions(clientId);

      // A subscription cannot be billed on itself.
      set(
        billingProjectsAtom,
        projects.filter((project) => project.id !== projectId),
      );
    } catch {
      set(billingProjectsAtom, []);
    }
  },
);

export const reloadPlacesAtom = atom(null, async (get, set): Promise<void> => {
  const contactId = get(projectEditorAtom).clientScope.contactId;

  if (null === contactId) {
    return;
  }

  const [locations, billingAddresses] = await Promise.all([
    projectApi.fetchLocations(contactId),
    projectApi.fetchBillingAddresses(contactId),
  ]);

  set(projectEditorAtom, (state) => {
    state.clientScope.locations = locations;
    state.clientScope.billingAddresses = billingAddresses;
  });
});

export const reloadClientContactsAtom = atom(
  null,
  async (_get, set, clientId: number): Promise<void> => {
    const contacts = await projectApi.fetchClientContacts(clientId);

    set(projectEditorAtom, (state) => {
      state.clientScope.contacts = contacts;
    });
  },
);

export const syncClosurePlannableAtom = atom(
  null,
  async (get, set): Promise<void> => {
    const { projectId, project } = get(projectEditorAtom);
    const subscription = project?.subscription ?? null;

    if (null === projectId || null === subscription) {
      return;
    }

    let plannable: boolean;

    try {
      const fresh = await projectApi.fetchProject(projectId);

      plannable = true === fresh.subscription?.closurePlannable;
    } catch {
      return;
    }

    if (plannable === subscription.closurePlannable) {
      return;
    }

    set(projectEditorAtom, (state) => {
      const saved = state.project?.subscription ?? null;

      if (null !== saved) {
        saved.closurePlannable = plannable;
      }
    });

    if (plannable) {
      set(editorTabAtom, "closure-weeks");
    }
  },
);

export const projectAtom = atom((get) => get(projectEditorAtom).project);

export const loadProjectEditorAtom = atom(
  null,
  async (
    _get,
    set,
    projectId: number | null,
    type: ProjectType,
    subscriptionId: number | null = null,
  ): Promise<void> => {
    set(projectEditorAtom, (state) => {
      state.projectId = projectId;
      state.type = type;
      state.loading = true;
      state.error = null;
      state.conflict = false;
    });

    set(editedExpenseAtom, null);

    try {
      const [options, project, parent] = await Promise.all([
        loadOptions(),
        null === projectId ? null : projectApi.fetchProject(projectId),
        null === projectId && null !== subscriptionId
          ? projectApi.fetchProject(subscriptionId)
          : null,
      ]);

      const empty = emptyProject(type);

      set(
        projectForm.actions.setInitial,
        null !== project
          ? toProjectValues(project)
          : null === parent
            ? empty
            : {
                ...empty,
                clientId: parent.clientId,
                intervention: {
                  ...empty.intervention,
                  subscriptionId: parent.id,
                },
              },
      );

      set(projectEditorAtom, (state) => {
        state.options = options;
        state.project = project;
        state.type = project?.type ?? type;
        state.loading = false;
      });

      const scopeOf = project ?? parent;

      await set(
        loadClientScopeAtom,
        scopeOf?.clientId ?? null,
        scopeOf?.clientContactId ?? null,
      );
    } catch (error: unknown) {
      set(projectEditorAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const saveProjectAtom = atom(
  null,
  async (get, set): Promise<number | null> => {
    const { projectId, type } = get(projectEditorAtom);

    set(projectEditorAtom, (state) => {
      state.error = null;
      state.conflict = false;
      state.saving = true;
    });

    let savedId: number | null = null;

    try {
      const result = await set(projectForm.actions.submit, async (values) => {
        const saved =
          null === projectId
            ? await projectApi.createProject(type, values)
            : await projectApi.updateProject(projectId, values);

        savedId = saved.id;

        set(projectForm.actions.setInitial, toProjectValues(saved));

        set(projectEditorAtom, (state) => {
          state.project = saved;
          state.projectId = saved.id;
          state.type = saved.type;
        });
      });

      if (!result.success) {
        if (0 < result.unbound.length) {
          const paths = result.unbound.join(", ");

          set(projectEditorAtom, (state) => {
            state.error = text(
              msg`The form cannot be saved: a check failed on ${paths}, which this form does not show. Report it.`,
            );
          });
        }

        return null;
      }
    } catch (error: unknown) {
      set(projectEditorAtom, (state) => {
        state.error = message(error);
        state.conflict = error instanceof ApiError && 409 === error.status;
      });

      if (error instanceof ApiError && 0 < error.violations.length) {
        set(projectForm.actions.setErrors, error.violations);
      }

      return null;
    } finally {
      set(projectEditorAtom, (state) => {
        state.saving = false;
      });
    }

    return savedId;
  },
);

async function loadOptions(): Promise<ProjectOptions> {
  const [
    statuses,
    serviceGroups,
    affiliates,
    taskTypes,
    unitTypes,
    accountantTypes,
    clientContactTypes,
    sites,
    expenseRecurrences,
    expenseTypes,
    paymentTypes,
    paymentPeriods,
  ] = await Promise.all([
    loadReferences("projectStatuses"),
    loadReferences("serviceGroups"),
    loadReferences("affiliates"),
    loadReferences("taskTypes"),
    loadReferences("unitTypes"),
    loadReferences("accountantTypes"),
    loadReferences("clientContactTypes"),
    loadReferences("sites"),
    loadReferences("expenseRecurrences"),
    projectApi.fetchExpenseTypes(),
    projectApi.fetchPaymentTypes(),
    loadReferences("paymentPeriods"),
  ]);

  return {
    statuses,
    serviceGroups,
    affiliates,
    taskTypes,
    unitTypes,
    accountantTypes,
    clientContactTypes,
    sites,
    expenseRecurrences,
    expenseTypes,
    paymentTypes,
    paymentPeriods,
  };
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
